import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { en } from './en';
import { ru } from './ru';
import { useAuth } from '../lib/auth';

export type Locale = 'ru' | 'en';

export const dictionaries: Record<Locale, typeof en> = { en, ru };

export const availableLocales: readonly Locale[] = ['ru', 'en'];

const DEFAULT_LOCALE: Locale = 'ru';

/** Union of all valid dot-path keys, derived from the `en` dictionary shape. */
type DotPaths<T> = {
  [K in keyof T & string]: T[K] extends string ? K : `${K}.${DotPaths<T[K]>}`;
}[keyof T & string];

export type TranslationKey = DotPaths<typeof en>;

export type TranslateParams = Record<string, string | number>;

export type TranslateFn = (key: TranslationKey, params?: TranslateParams) => string;

function storageKey(userId: string | null): string {
  return `rf-crm:locale:${userId ?? 'guest'}`;
}

function readStored(userId: string | null): Locale | null {
  try {
    const v = window.localStorage.getItem(storageKey(userId));
    return v === 'ru' || v === 'en' ? v : null;
  } catch {
    return null;
  }
}

function getPath(dict: unknown, path: string): string | undefined {
  // Defensive: a runtime key that is not a non-empty string (e.g. a lookup
  // that missed its map) must yield "missing", never throw on `.split`.
  if (typeof path !== 'string' || path.length === 0) return undefined;
  let cur: unknown = dict;
  for (const part of path.split('.')) {
    if (typeof cur !== 'object' || cur === null) return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return typeof cur === 'string' ? cur : undefined;
}

function translate(locale: Locale, key: string, params?: TranslateParams): string {
  // EN is the runtime fallback if a key is ever missing in another locale
  // (can't happen statically — `ru` is typed as `typeof en` — but keeps the
  // UI safe if a dictionary is ever edited by hand).
  let value = getPath(dictionaries[locale], key) ?? getPath(dictionaries.en, key) ?? key ?? '';
  if (params) {
    for (const [name, param] of Object.entries(params)) {
      value = value.replaceAll(`{${name}}`, String(param));
    }
  }
  return value;
}

interface LanguageContextValue {
  t: TranslateFn;
  locale: Locale;
  setLocale: (next: Locale) => void;
  availableLocales: readonly Locale[];
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export interface LanguageProviderProps {
  /**
   * Current user id — the locale is stored per-user in localStorage under
   * `rf-crm:locale:<userId>`. `null` (logged out) uses the `guest` key.
   */
  userId?: string | null;
  children: ReactNode;
}

export function LanguageProvider({ userId = null, children }: LanguageProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(() => readStored(userId) ?? DEFAULT_LOCALE);

  // Identity change (login/logout): switch to that user's stored locale.
  // No saved value → default 'ru'. We deliberately do NOT inherit the guest
  // choice into the user's key — per-user isolation stays predictable.
  useEffect(() => {
    setLocaleState(readStored(userId) ?? DEFAULT_LOCALE);
  }, [userId]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback(
    (next: Locale) => {
      try {
        window.localStorage.setItem(storageKey(userId), next);
      } catch {
        // private mode / quota — locale still applies for the session
      }
      setLocaleState(next);
    },
    [userId],
  );

  const t = useCallback<TranslateFn>(
    (key, params) => translate(locale, key, params),
    [locale],
  );

  const value = useMemo<LanguageContextValue>(
    () => ({ t, locale, setLocale, availableLocales }),
    [t, locale, setLocale],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

/**
 * App-level wrapper: resolves the current user id from auth and mounts the
 * LanguageProvider with it. Must live INSIDE AuthProvider (see main.tsx).
 */
export function LanguageRoot({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  return <LanguageProvider userId={user?.id ?? null}>{children}</LanguageProvider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useT(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useT must be used within a LanguageProvider');
  return ctx;
}
